import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import GoogleIcon from '@mui/icons-material/Google';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import CloudIcon from '@mui/icons-material/Cloud';
import StorageIcon from '@mui/icons-material/Storage';
import DnsIcon from '@mui/icons-material/Dns';
import DataObjectIcon from '@mui/icons-material/DataObject';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PeopleIcon from '@mui/icons-material/People';
import HubIcon from '@mui/icons-material/Hub';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import SchemaIcon from '@mui/icons-material/Schema';

import { Menu, MenuItem, CircularProgress } from '@mui/material';
import { clearStoredAuthTokens } from '../utils/auth';
import Sidebar from './Sidebar';
import { dsVar, ink } from '../theme/tokens';

interface Integration {
    id: string;
    _id?: string;
    integrationName?: string;
    displayName: string;
    provider: string;
    status: string;
    integrationType?: string;
}

const LinkedDataApps = () => {
    const navigate = useNavigate();
    const [myConnections, setMyConnections] = useState<Integration[]>([]);
    const [loading, setLoading] = useState(true);
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);

    const handleUserMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        setUserMenuAnchorEl(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchorEl(null);
    };

    const handleSignOut = () => {
        clearStoredAuthTokens();
        handleUserMenuClose();
        navigate('/login');
    };

    useEffect(() => {
        const fetchMyConnections = async () => {
            setLoading(true);
            try {
                const orgId = localStorage.getItem('org_id');
                if (!orgId) {
                    setLoading(false);
                    return;
                }
                const response = await axiosInstance.get(
                    `/integrations?orgId=${orgId}&page=1&limit=5`
                );
                if (response.data && response.data.integrations) {
                    setMyConnections(response.data.integrations);
                }
            } catch (error) {
                console.error("Failed to fetch connections:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchMyConnections();
    }, []);

    const connectionCategories = [
        {
            title: 'Transaction Data',
            description: 'Core banking and structured financial record databases.',
            items: [
                {
                    id: 'postgres',
                    name: 'PostgreSQL',
                    description: 'Structured transaction logs and customer records.',
                    icon: <StorageIcon sx={{ color: ink('#336791') }} />
                },
                {
                    id: 'mysql',
                    name: 'MySQL',
                    description: 'Legacy financial systems and ledger data.',
                    icon: <StorageIcon sx={{ color: ink('#00758F') }} />
                },
                {
                    id: 'mssql',
                    name: 'MSSQL',
                    description: 'Enterprise banking environments and financial records.',
                    icon: <StorageIcon sx={{ color: ink('#CC2927') }} />
                },
                {
                    id: 'oracle',
                    name: 'Oracle',
                    description: 'Institutional banking data and compliance logs.',
                    icon: <DnsIcon sx={{ color: ink('#F80000') }} />
                },
                {
                    id: 'mongodb',
                    name: 'MongoDB',
                    description: 'Semi-structured session logs and banking metadata.',
                    icon: <DataObjectIcon sx={{ color: ink('#47A248') }} />
                }
            ]
        },
        {
            title: 'Payment & Fintech Integration',
            description: 'Aggregators and digital payment processing platforms.',
            items: [
                {
                    id: 'plaid',
                    name: 'Plaid',
                    description: 'Bank account history and identity verification.',
                    icon: <AccountBalanceIcon sx={{ color: '#000000' }} />
                },
                {
                    id: 'stripe',
                    name: 'Stripe',
                    description: 'Digital payment flows and merchant risk profiles.',
                    icon: <PaymentsIcon sx={{ color: ink('#635BFF') }} />
                },
                {
                    id: 'paypal',
                    name: 'PayPal',
                    description: 'Global peer-to-peer and merchant transaction data.',
                    icon: <AccountBalanceWalletIcon sx={{ color: ink('#003087') }} />
                },
                {
                    id: 'braintree',
                    name: 'Braintree',
                    description: 'Mobile and web payment processing data.',
                    icon: <PaymentIcon sx={{ color: '#000000' }} />
                },
                {
                    id: 'square',
                    name: 'Square',
                    description: 'Point-of-sale (POS) transaction patterns.',
                    icon: <CropSquareIcon sx={{ color: '#000000' }} />
                }
            ]
        },
        {
            title: 'External Risk & Market Intelligence',
            description: 'Real-time market data and crypto-asset monitoring.',
            items: [
                {
                    id: 'alpha-vantage',
                    name: 'Alpha Vantage',
                    description: 'Market data for layering and wealth change tracking.',
                    icon: <TrendingUpIcon sx={{ color: ink('#2C3E50') }} />
                },
                {
                    id: 'coinapi',
                    name: 'CoinAPI',
                    description: 'AML tracking for digital asset prices and volatility.',
                    icon: <CurrencyBitcoinIcon sx={{ color: ink('#F7931A') }} />
                },
                {
                    id: 'polygon',
                    name: 'Polygon',
                    description: 'Cross-reference stock trades with cash movements.',
                    icon: <ShowChartIcon sx={{ color: ink('#7B3FE4') }} />
                }
            ]
        },
        {
            title: 'Identity & Verification (KYC/KYB)',
            description: 'Customer due diligence and risk-tagging platforms.',
            items: [
                {
                    id: 'salesforce',
                    name: 'Salesforce',
                    description: 'Customer Due Diligence (CDD) and KYC documentation.',
                    icon: <PeopleIcon sx={{ color: ink('#00A1E0') }} />
                },
                {
                    id: 'hubspot',
                    name: 'HubSpot',
                    description: 'Fintech onboarding and risk-tagging metadata.',
                    icon: <HubIcon sx={{ color: ink('#FF7A59') }} />
                },
                {
                    id: 'zendesk-talk',
                    name: 'Zendesk Talk',
                    description: 'Customer interaction logs for SAR context.',
                    icon: <HeadsetMicIcon sx={{ color: ink('#03363D') }} />
                }
            ]
        },
        {
            title: 'Unstructured & External Document Data',
            description: 'Raw logs, identity docs, and organizational directory data.',
            items: [
                {
                    id: 's3',
                    name: 'Amazon S3',
                    description: 'Raw logs, PDF identification, and CSV exports.',
                    icon: <CloudIcon sx={{ color: ink('#FF9900') }} />
                },
                {
                    id: 'google-directory',
                    name: 'Google Directory',
                    description: 'Employee fraud monitoring and PEP identification.',
                    icon: <FolderSharedIcon sx={{ color: ink('#34A853') }} />
                },
                {
                    id: 'azure-blob',
                    name: 'Azure Blob Storage',
                    description: 'Compliance evidence in Microsoft environments.',
                    icon: <CloudQueueIcon sx={{ color: ink('#008AD7') }} />
                }
            ]
        }
    ];

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg selection:bg-accent selection:text-on-accent">
            <Sidebar activePage="linked-data" />
            
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top Navigation Bar */}
                <header className="h-16 border-b border-line/50 bg-page/80 backdrop-blur-md px-8 flex items-center justify-between sticky top-0 z-10 m-0">
                    <div className="flex-1 max-w-2xl text-left">
                        <div className="relative group flex items-center justify-start h-full">
                            <SearchIcon className="absolute left-3 text-fg-muted group-focus-within:text-accent-fg transition-colors" />
                            <input 
                                className="w-full bg-surface border-none rounded-lg py-2 pl-10 pr-4 text-sm focus:ring-1 focus:ring-accent-fg placeholder:text-fg-muted text-fg outline-none" 
                                placeholder="Search connections..." 
                                type="text" 
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-4 ml-8">
                        <button className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center">
                            <NotificationsIcon />
                        </button>
                        <div
                            className="p-2 text-fg-2 hover:text-accent-fg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
                            onClick={handleUserMenuOpen}
                            aria-label="Open user menu"
                        >
                            <AccountCircleIcon />
                        </div>
                        <Menu
                            anchorEl={userMenuAnchorEl}
                            open={Boolean(userMenuAnchorEl)}
                            onClose={handleUserMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                sx: {
                                    borderRadius: '12px',
                                    backgroundColor: dsVar('surface'),
                                    color: dsVar('fg-body'),
                                    border: `1px solid ${dsVar('line')}`,
                                    minWidth: '180px',
                                    mt: 1
                                }
                            }}
                        >
                            <MenuItem
                                disabled
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: dsVar('fg-2'),
                                    borderBottom: `1px solid ${dsVar('line')}`,
                                    opacity: '1 !important'
                                }}
                            >
                                {localStorage.getItem('org_id') || 'User'}
                            </MenuItem>
                            <MenuItem
                                onClick={handleSignOut}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    fontSize: '0.9rem',
                                    color: dsVar('accent-fg'),
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 222, 89, 0.08)'
                                    }
                                }}
                            >
                                <LogoutIcon fontSize="small" />
                                Sign out
                            </MenuItem>
                        </Menu>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto">
                    <main className="flex-1 flex flex-col p-6 lg:p-10 w-full xl:max-w-7xl mx-auto m-0 text-left">
                        {/* Hero Section */}
                        <div className="mb-10 flex flex-col gap-4 m-0 text-left items-start">
                            <h1 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight text-fg m-0 mr-auto p-0 font-mono">
                                Linked Data/ <span className="text-fg font-normal m-0" style={{ fontFamily: 'Inter, sans-serif' }}>Apps</span>
                            </h1>
                            <p className="text-fg-2 text-lg max-w-2xl font-light leading-relaxed m-0 p-0 tracking-normal font-sans">
                                Manage and connect your organizational data sources and applications to power deep AI insights.
                            </p>
                        </div>

                        {/* My Connections Section */}
                        <div className="mb-8 flex flex-col gap-4 m-0 text-left items-start">
                            <h2 className="text-2xl font-bold text-fg m-0 font-mono" style={{ color: dsVar('accent-fg') }}>
                                My <span className="text-fg font-normal" style={{ fontFamily: 'Inter, sans-serif' }}>connections</span>
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 m-0 w-full mb-12">
                            {loading ? (
                                <div className="col-span-full flex justify-center py-12">
                                    <CircularProgress style={{ color: dsVar('accent-fg') }} />
                                </div>
                            ) : myConnections.length > 0 ? (
                                myConnections.map(conn => (
                                    <div key={conn.id} className="group relative bg-surface border border-line rounded-xl overflow-hidden hover:border-accent-fg/50 transition-all duration-300 m-0">
                                        <div className="p-6 flex flex-col h-full m-0 text-left items-start">
                                            <div className="flex justify-between items-start mb-6 w-full m-0">
                                                <div className="w-12 h-12 rounded-lg bg-accent/20 border border-accent-fg/30 flex items-center justify-center text-accent-fg group-hover:scale-110 transition-transform m-0">
                                                    <SettingsInputComponentIcon className="text-2xl m-0 flex items-center justify-center font-bold" />
                                                </div>
                                                <span className={`text-[10px] font-mono px-2 py-1 rounded-full border ${conn.status === 'active' ? 'text-green-400 border-green-400/30 bg-green-400/10' : 'text-fg-muted border-fg-muted/30 bg-fg-muted/10'} uppercase tracking-tighter m-0`}>
                                                    {conn.status}
                                                </span>
                                            </div>
                                            <h3 className="text-xl font-bold text-fg mb-2 leading-snug m-0 w-full font-mono">{conn.displayName}</h3>
                                            {conn.integrationName && (
                                                <p className="text-fg-2 text-sm mb-4 leading-relaxed m-0 font-sans tracking-tight">{conn.integrationName}</p>
                                            )}
                                            <span className="text-[10px] font-mono text-fg-muted uppercase tracking-wider m-0">{conn.provider}</span>
                                            <div className="mt-auto pt-4 border-t border-fg/5 w-full m-0">
                                                <button className="text-accent-fg text-sm font-bold hover:underline bg-transparent border-none cursor-pointer p-0">
                                                    Manage Connection
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-full py-12 px-6 bg-surface/30 border border-dashed border-line rounded-xl text-center">
                                    <p className="text-fg-2 m-0">No connections linked yet. Explore available connectors below.</p>
                                </div>
                            )}
                        </div>

                        {/* Available Connections Section */}
                        <div className="mb-8 flex flex-col gap-4 m-0 text-left items-start">
                            <h2 className="text-2xl font-bold text-fg m-0 font-mono" style={{ color: dsVar('accent-fg') }}>
                                Available <span className="text-fg font-normal" style={{ fontFamily: 'Inter, sans-serif' }}>connections</span>
                            </h2>
                        </div>

                        <div className="space-y-12 w-full mb-12">
                            {connectionCategories.map(category => (
                                <div key={category.title} className="flex flex-col gap-6">
                                    <div className="flex flex-col gap-1 text-left">
                                        <h3 className="text-xl font-bold text-fg m-0 font-mono" style={{ color: dsVar('accent-fg') }}>{category.title}</h3>
                                        <p className="text-sm text-fg-muted m-0">{category.description}</p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 m-0 w-full">
                                        {category.items.map(conn => (
                                            <div key={conn.id} className="group relative bg-surface border border-line rounded-xl overflow-hidden hover:border-accent-fg/50 transition-all duration-300 m-0 cursor-pointer">
                                                <div className="p-6 flex flex-col h-full m-0 text-left items-start">
                                                    <div className="flex justify-between items-start mb-6 w-full m-0">
                                                        <div className="w-12 h-12 rounded-lg bg-fg/5 flex items-center justify-center group-hover:scale-110 transition-transform m-0 border border-line">
                                                            {conn.icon}
                                                        </div>
                                                    </div>
                                                    <h3 className="text-xl font-bold text-fg mb-2 leading-snug m-0 w-full font-mono">{conn.name}</h3>
                                                    <p className="text-fg-2 text-sm mb-6 leading-relaxed m-0 font-sans tracking-tight">{conn.description}</p>
                                                    <div className="mt-auto pt-4 border-t border-fg/5 w-full m-0">
                                                        <div className="flex items-center text-accent-fg text-sm font-bold group-hover:translate-x-1 transition-transform">
                                                            Connect now
                                                            <span className="ml-2">→</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default LinkedDataApps;
